import { TChannel } from '../api/messages';

/**
 * PubNubで受け取った通知が、チャットの利用者(LINEユーザー)からの送信かを判定する。
 * 未読は利用者の送信だけを数える仕様のため、管理側（自分・他スタッフ・自動投稿）の
 * 送信をここで弾き、一覧のバッジをサイドバーの総数と同じ定義に保つ。
 * @param channels 一覧に表示中のチャンネル（participantsは利用者のみ返る）
 * @param channelId 通知が指すチャンネルID
 * @param publisher PubNubのpublisher UUID
 * @returns 利用者からの送信ならtrue
 */
export function isMessageFromChatUser(
  channels: TChannel[],
  channelId: string,
  publisher?: string,
): boolean {
  if (!publisher) return false;

  return channels.some(
    (channel) =>
      channel.id === channelId &&
      channel.participants?.some(
        (participant) =>
          participant.uuid === publisher &&
          participant.provider === 'MALE_APPLICATION',
      ),
  );
}

/**
 * 利用者側が未読台帳へ記録できないまま送った通知かを判定する。
 * 台帳に無い送信はサーバの合計に入らないため、一覧のバッジもローカルに増やさない
 * （増やすと合計と食い違ったまま戻らない）。新着自体は通知音で知らせる。
 * @param payload PubNubで受け取ったメッセージ
 * @returns 未記録の送信ならtrue
 */
export function isUnrecordedChannelNotification(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;

  return (payload as { unrecorded?: unknown }).unrecorded === true;
}

/**
 * 既読位置をサーバへ記録し、完了後に総未読を取り直す。
 * 呼び出し元は行のバッジを先にローカルで0にするため、記録に失敗したら
 * その上書きを捨ててサーバ値へ戻す（行だけ0のまま合計に未読が残ると、
 * 一覧とサイドバーが食い違ったまま戻らない）。
 * @param params.channelId 既読にするチャンネルID
 * @param params.updateSeenTime 既読位置を記録するAPI
 * @param params.onFailure 記録に失敗したときにローカルの上書きを捨てる処理
 * @param params.onSettled 成否によらず総未読を取り直す処理
 */
export async function runSeenTimeSync(params: {
  channelId: string;
  updateSeenTime: (channelId: string) => Promise<unknown>;
  onFailure: (channelId: string) => void;
  onSettled: () => void;
}): Promise<void> {
  try {
    await params.updateSeenTime(params.channelId);
  } catch (error) {
    console.error('Failed to update seen time:', error);
    params.onFailure(params.channelId);
  } finally {
    params.onSettled();
  }
}
